import React, { useState } from 'react';
import { Button, Table, Space, Modal, Tag, message, Alert, Empty, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, PhoneOutlined, RobotOutlined } from '@ant-design/icons';
import MappingForm from './MappingForm';
import apiClient from '../api';

const MappingManager = ({ mappings, templates, loading, onUpdate }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleCreate = () => {
    setIsModalVisible(true);
  };

  const handleDelete = (mapping) => {
    if (!mapping) return;
    console.log('[MappingManager] Delete clicked for mapping:', mapping);
    setDeleteTarget(mapping);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const phoneNumber = deleteTarget.phoneNumber;
    if (!phoneNumber) return;

    try {
      const response = await apiClient.delete(
        `/mappings/${encodeURIComponent(phoneNumber)}`
      );
      if (response.data.success) {
        message.success('Mapping deleted successfully');
        setDeleteTarget(null);
        onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete mapping');
      console.error(error);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      const response = await apiClient.post('/mappings', values);

      if (response.data.success) {
        message.success('Mapping created successfully');
        setIsModalVisible(false);
        onUpdate();
      } else {
        message.error(response.data.message || 'Failed to create mapping');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create mapping');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Bot Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 300,
      render: (text) => (
        <Space>
          <PhoneOutlined style={{ color: '#667eea' }} />
          <Tag color="blue" style={{ fontSize: 14 }}>{text}</Tag>
        </Space>
      ),
    },
    {
      title: 'Assigned Agent',
      dataIndex: 'templateId',
      key: 'templateId',
      render: (templateId) => {
        const template = templates.find(t => t.templateId === templateId);
        return (
          <Space direction="vertical" size={2}>
            <Tag color="green" icon={<RobotOutlined />}>
              {templateId}
            </Tag>
            {template && (
              <span style={{ fontSize: 12, color: '#666' }}>
                {template.agentName}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Functions',
      dataIndex: 'templateId',
      key: 'functions',
      render: (templateId) => {
        const template = templates.find(t => t.templateId === templateId);
        return template ? (
          <Tag color="purple">{template.enabledFunctions?.length || 0} functions</Tag>
        ) : (
          <Tag color="default">-</Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button 
          type="link" 
          danger 
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0 }}>📱 Bot Number Mappings</h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Assign agent templates to your bot's WhatsApp numbers
            </p>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              size="large"
              onClick={handleCreate}
              disabled={templates.length === 0}
            >
              Map Bot Number
            </Button>
          </Col>
        </Row>
      </div>

      <Alert
        message="How it works"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>Map your bot's receiving numbers to different agent templates (like different departments)</strong>
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Bot number +1-234-5678 → Travel Agent</li>
              <li>Bot number +1-987-6543 → Support Agent</li>
            </ul>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              💡 When customers message a bot number, they get that number's assigned agent
            </p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {templates.length === 0 && (
        <Alert
          message="No Templates Available"
          description="You need to create at least one agent template before creating mappings. Go to the 'Agent Templates' tab to create one."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table 
        columns={columns} 
        dataSource={mappings}
        rowKey="phoneNumber"
        loading={loading}
        pagination={{ pageSize: 10 }}
        locale={{
          emptyText: (
            <Empty 
              description="No mappings yet. Map your bot numbers to agent templates!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Bot Number Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to remove this bot number mapping? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>Bot Phone Number:</strong>{' '}
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {deleteTarget.phoneNumber}
            </Tag>
          </p>
        )}
      </Modal>

      <Modal
        title="➕ Map Bot Number to Agent"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <MappingForm 
          templates={templates}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>
    </div>
  );
};

export default MappingManager;

